import { http } from '../http';
import { extrairMensagemErro } from '../../utils/apiError';
import { SituacaoEnvioFiltro } from '../associado/associadoApi';

export interface VistoriaEnvioSga {
  id: string;
  placa: string;
  modelo: string;
  associado: string;
  consultor: string;
  codigoAssociado: string | null;
  codigoVeiculo: string | null;
  addedToSga: boolean; // laudo
  imagesAddedToSga: boolean; // fotos
  createdat: string;
}

export interface AcompanhamentoVistoriasResponse {
  totalItems: number;
  page: number;
  limit: number;
  totais: { vistoriasPendentes: number; fotosPendentes: number };
  items: VistoriaEnvioSga[];
}

const normalizePlaca = (placa: string) =>
  (placa ?? '').toString().trim().toUpperCase().replace(/[^A-Z0-9]/g, '');

/** Erro de envio ao SGA carregando o status HTTP. */
export interface ErroEnvioSga extends Error {
  status: number | null;
}

/**
 * Preserva o status HTTP no Error (at-31).
 *
 * A mensagem continua idêntica — quem só lê `.message` (tela /ti/envios) não muda de
 * comportamento. A tela do laudo precisa do status para separar três desfechos que o
 * consultor lê de formas bem diferentes: 409 "já enviado" (é sucesso), 400 "veículo
 * ainda não está no SGA" (o cron resolve) e falha de rede/SGA (tentar de novo depois).
 */
const erroComStatus = (error: unknown, fallback: string): ErroEnvioSga => {
  const err = new Error(extrairMensagemErro(error, fallback)) as ErroEnvioSga;
  err.status = (error as any)?.response?.status ?? null;
  return err;
};

export const fetchAcompanhamentoVistorias = async (
  page = 1,
  limit = 10,
  busca?: string,
  situacao: SituacaoEnvioFiltro = 'TODOS',
): Promise<AcompanhamentoVistoriasResponse> => {
  try {
    const termo = busca?.trim();
    const res = await http.get('/sga-vistoria/acompanhamento', {
      params: {
        page,
        limit,
        ...(termo ? { busca: termo } : {}),
        ...(situacao && situacao !== 'TODOS' ? { situacao } : {}),
      },
    });
    return res.data;
  } catch (error: unknown) {
    throw new Error(extrairMensagemErro(error, 'Erro ao buscar acompanhamento de vistorias'));
  }
};

export const enviarVistoriaPorId = async (id: string): Promise<any> => {
  try {
    const res = await http.post(`/sga-vistoria/${id}/sincronizar`, {});
    return res.data;
  } catch (error: unknown) {
    throw erroComStatus(error, 'Erro ao enviar vistoria ao SGA');
  }
};

export const enviarFotosVistoriaPorPlaca = async (placa: string): Promise<any> => {
  const placaNorm = normalizePlaca(placa);
  if (!placaNorm) throw new Error('Placa é obrigatória.');
  try {
    const res = await http.post(`/sga-vistoria/enviarImagensParaSga/${placaNorm}`, {});
    return res.data;
  } catch (error: unknown) {
    throw erroComStatus(error, 'Erro ao enviar fotos da vistoria ao SGA');
  }
};
